import { useEffect, useRef } from "react";
import flashlightPng from "@images/flashlight.png";

type FlashlightLoaderProps = {
  title?: string;
  subtitle?: string;
  className?: string;
};

const getRandomInRange = (min: number, max: number): number => {
  return Math.random() * (max - min) + min;
};

const getRandomWithMinMagnitude = (min: number, max: number, minMagnitude: number): number => {
  let value = getRandomInRange(min, max);
  if (Math.abs(value) < minMagnitude) {
    value = value < 0 ? -minMagnitude : minMagnitude;
  }
  return value;
};

const FlashlightLoader = ({
  title = "Finding your newsletters",
  subtitle = "Sit back while we work our magic and round up all your newsletters",
  className,
}: FlashlightLoaderProps) => {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const animationTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    // Increase visibility slightly on web while staying subtle
    const maxDistancePx = 14; // total range ±14px
    const minStepMagnitudePx = 6; // guarantee each step is noticeable

    const step = () => {
      const el = imageRef.current;
      if (!el) return;
      const x = getRandomWithMinMagnitude(-maxDistancePx, maxDistancePx, minStepMagnitudePx);
      const y = getRandomWithMinMagnitude(-maxDistancePx, maxDistancePx, minStepMagnitudePx);
      const durationSec = getRandomInRange(2.0, 4.0); // iOS: slower, subtle

      // Apply transition for this leg and animate to a new random offset
      el.style.transition = `transform ${durationSec}s linear`;
      el.style.transform = `translate3d(${x}px, ${y}px, 0)`;

      animationTimeoutRef.current = window.setTimeout(() => {
        step();
      }, durationSec * 1000);
    };

    // Ensure the very first movement begins immediately after mount.
    const el = imageRef.current;
    if (el) {
      // Establish a known starting point with no transition, then animate on next frame.
      el.style.transition = "transform 0s";
      el.style.transform = "translate3d(0px, 0px, 0)";
      // Kick off the first animated step on the next frame to avoid any start delay.
      requestAnimationFrame(() => {
        step();
      });
    }

    return () => {
      if (animationTimeoutRef.current) {
        window.clearTimeout(animationTimeoutRef.current);
      }
      if (imageRef.current) {
        imageRef.current.style.transition = "";
        imageRef.current.style.transform = "translate3d(0px, 0px, 0)";
      }
    };
  }, []);

  return (
    <div className={`flex flex-col items-center justify-center ${className ?? ""}`}>
      <img
        ref={imageRef}
        src={flashlightPng}
        className="w-[220px] h-auto select-none will-change-transform"
        draggable={false}
      />
      <div className="text-center mt-2 px-6">
        <div className="text-white font-primary font-bold text-2xl">{title}</div>
        <div className="text-white/60 font-primary font-medium text-base mt-2">{subtitle}</div>
      </div>
    </div>
  );
};

export default FlashlightLoader;


